// @ts-nocheck
/*
 * Port of views/dashboard/campaigns/campaign-report.js (753 lines).
 * BYTE-IDENTICAL logic — server-fetched paginated call-report with sortable
 * columns and filter-driven aggregate refetch (`includeSummary`).
 *
 * Table swap note: v1 uses PrimeReact DataTable in lazy mode (server-side
 * pagination via `first`/`totalRecords`/`onPage`/`onSort`). obd3-gui DataTable
 * is client-side only, so we render a plain react-bootstrap Table with the
 * same sort click + manual bootstrap pager beneath. Server-side page/limit/
 * sort state, filter reset behavior, and export-current-page semantics are
 * preserved exactly as v1.
 *
 * PrimeReact Button swapped for react-bootstrap Button (v1 already imports
 * both — v2 keeps only the bootstrap one).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button as BsButton } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  FiArrowLeft,
  FiPhone,
  FiPhoneCall,
  FiCheckCircle,
  FiSlash,
  FiXCircle,
  FiPhoneOff,
  FiHash,
  FiClock,
  FiMic,
  FiUsers,
  FiRefreshCw,
} from 'react-icons/fi';
import Loader from '@/components/partials/components/Loader';
import { getApiCall } from '@/service/httpCall';
import { API_CAMPAIGN_REPORT, API_HANGUP_CAUSES } from '@/service/url';
import { ROUTES, USER_ROLES } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import { exportToCSV } from '@/utilities/helpers';
import { errorCheck } from '@/views/dashboard/auth/auth-utils';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';
import { CALL_STATUS_LABELS } from '@/utilities/callStatus';
import CdrCallDetailDialog from '@/features/ivr-reports/components/CdrCallDetailDialog';
import { formatIvrJourneyStep, getIvrJourneyForCalls } from '@/features/ivr-reports/DidReportDetailPage';

const DIALER_TYPE_LABEL = {
  1: 'Click2Call',
  2: 'Auto Dialer',
  3: 'Predictive Dialer',
  4: 'Power Dialer',
};

const TEMPLATE_LABEL = { 0: 'Simple IVR', 1: 'DTMF', 2: 'Call Patch', 3: 'Custom IVR' };

const CAMPAIGN_STATUS_LABEL = {
  0: 'Ready',
  1: 'Running',
  2: 'Completed',
  3: 'Low Balance',
  4: 'Error',
  5: 'Error',
  6: 'Cancelling',
  7: 'Cancelled',
  8: 'Pausing',
  9: 'Paused',
  10: 'Resuming',
  21: 'DND Processing',
  22: 'Waiting for Channel',
};

// CDR call_status (backend call_outcome: customer leg's call_status, else billing > 0 for old rows).
const callStatusOf = (r) => CALL_STATUS_LABELS[String(r.call_outcome || '').toLowerCase()] || 'Failed';

const CALL_STATUS_OPTIONS = [
  { value: 'all', label: 'All Calls' },
  { value: 'connected', label: 'Connected' },
  { value: 'failed', label: 'Failed' },
  { value: 'abandoned', label: 'Abandoned' },
];

// Still dialing: Running, Resuming, DND Processing, Waiting for Channel.
const LIVE_CAMPAIGN_STATUSES = new Set([1, 10, 21, 22]);
const AUTO_REFRESH_MS = 20000;

const toSec = (v) => {
  const t = v ? Date.parse(String(v).replace(' ', 'T')) : NaN;
  return Number.isNaN(t) ? null : t / 1000;
};
// Leg total: CDR total_duration, else end - start.
const legTotalOf = (leg) => {
  if (!leg) return 0;
  if (leg.total_duration != null) return Number(leg.total_duration) || 0;
  const start = toSec(leg.start_time);
  const end = toSec(leg.end_time);
  return start != null && end != null && end >= start ? Math.round(end - start) : 0;
};
const agentLegsOf = (r) => (Array.isArray(r.agent_legs) ? r.agent_legs : []);
const legStatusOf = (leg) => CALL_STATUS_LABELS[String(leg?.call_status || '').toLowerCase()] || '—';
const ivrStepsOf = (r) =>
  getIvrJourneyForCalls({ ivr_journey: r.ivr_journey }).map((e) => {
    const step = formatIvrJourneyStep(e);
    const detail = step.detailItems.find((i) => !i.isLink);
    return detail ? `${step.title} (${detail.value})` : step.title;
  });
const agentsDialedText = (r) =>
  (Array.isArray(r.agents_dialed) ? r.agents_dialed : [])
    .map((a) => `${a.agent_name || a.agent || '-'}${a.state ? ` (${a.state})` : ''}`)
    .join(', ');
// Stable reference: the dialog refetches whenever pageRows changes identity.
const NO_PAGE_ROWS = [];

const DIRECTION_OPTIONS = [
  { value: 'all', label: 'Both Legs' },
  { value: 'outbound', label: 'Outbound' },
  { value: 'incoming', label: 'Incoming' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const formatDuration = (seconds) => {
  const total = Number(seconds || 0);
  if (!Number.isFinite(total) || total <= 0) return '0s';
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (!match) return String(value);
  const [, y, mo, d, h, mi, s] = match;
  const hour24 = parseInt(h, 10);
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  return `${d}/${mo}/${y}, ${String(hour12).padStart(2, '0')}:${mi}:${s} ${ampm}`;
};

const formatDateOnly = (value) => {
  if (!value) return '—';
  const asDate = new Date(`${value}T00:00:00`);
  if (Number.isNaN(asDate.getTime())) return String(value);
  return asDate.toLocaleDateString('en-IN');
};

const formatTimeOnly = (value) => {
  if (!value) return '—';
  return String(value).slice(0, 5);
};

const parseRecordings = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const StatTile = ({ icon, label, value, hint, tone: _tone = 'blue' }: any) => (
  <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-card p-3 shadow-neu-raised">
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
      {icon}
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="truncate text-lg font-bold tabular-nums text-foreground">
        {value}
      </div>
      {hint ? (
        <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div>
      ) : null}
    </div>
  </div>
);

const CampaignReport = () => {
  const { id: campaignId } = useParams();
  const navigate = useNavigate();

  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);

  const [loading, setLoading] = useState(true);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [campaign, setCampaign] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [hangupBreakdown, setHangupBreakdown] = useState<any[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [sortBy, setSortBy] = useState('customer_start_time');
  const [sortDir, setSortDir] = useState('desc');

  const [filters, setFilters] = useState({
    search: '',
    direction: 'all',
    call_status: 'all',
    hangup_cause: '',
    start_date: '',
    end_date: '',
  });
  const [draftSearch, setDraftSearch] = useState('');
  const [hangupCauseMap, setHangupCauseMap] = useState<Record<string, string>>({});
  const [detailRow, setDetailRow] = useState<any>(null);

  const summaryLoadedRef = useRef(false);
  const fetchingRef = useRef(false);
  const userRole = getUser()?.role;
  // Same as the DID/CDR export: super admins see each company's share, others one number.
  const showCompanyNames = userRole === USER_ROLES.SUPERADMIN || userRole === USER_ROLES.SUB_SUPERADMIN;

  useEffect(() => {
    getApiCall(API_HANGUP_CAUSES)
      .then((res: any) => {
        const list = Array.isArray(res) ? res : res?.data || [];
        const map: Record<string, string> = {};
        list.forEach((c: any) => {
          map[String(c.hangup_cause_code)] = c.message || c.hangup_cause_reason || String(c.hangup_cause_code);
        });
        setHangupCauseMap(map);
      })
      .catch(() => {});
  }, []);

  const hangupCauseLabel = useCallback(
    (code: any) => {
      if (!code || code === 'UNKNOWN') return 'Unknown';
      return hangupCauseMap[String(code)] || String(code);
    },
    [hangupCauseMap],
  );

  const onError = useCallback(
    (err) => {
      if (typeof err !== 'object' && String(err).includes('TokenExpiredError')) {
        errorCheck(err, navigate, setToastProps, setShowToast);
      } else {
        toast.error(friendlyApiError(err));
      }
    },
    [navigate],
  );

  const fetchReport = useCallback(
    async ({ includeSummary, silent = false }) => {
      if (!campaignId) return;
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('limit', String(limit));
      params.append('sort_by', sortBy);
      params.append('sort_dir', sortDir);
      params.append('include_summary', includeSummary ? '1' : '0');
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params.append(key, String(value));
        }
      });
      const url = `${API_CAMPAIGN_REPORT.replace(':id', campaignId)}?${params.toString()}`;
      if (silent && fetchingRef.current) return;
      if (!silent) {
        if (includeSummary) setLoading(true);
        setRowsLoading(true);
      }
      fetchingRef.current = true;
      try {
        const resp = await getApiCall(url, {});
        setCampaign(resp?.campaign || null);
        setRows(Array.isArray(resp?.rows) ? resp.rows : []);
        setTotalRecords(Number(resp?.pagination?.total || 0));
        if (resp?.summary) {
          setSummary(resp.summary);
          summaryLoadedRef.current = true;
        }
        if (Array.isArray(resp?.hangup_breakdown)) {
          setHangupBreakdown(resp.hangup_breakdown);
        }
      } catch (err) {
        // Background refresh keeps the current rows; the next tick retries.
        if (silent) return;
        onError(err);
        setRows([]);
        setTotalRecords(0);
      } finally {
        fetchingRef.current = false;
        if (!silent) {
          setLoading(false);
          setRowsLoading(false);
        }
      }
    },
    [campaignId, page, limit, sortBy, sortDir, filters, onError],
  );

  useEffect(() => {
    fetchReport({ includeSummary: !summaryLoadedRef.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, page, limit, sortBy, sortDir, filters]);

  // Live campaign: re-pull rows + summary every 20s (skipped while the tab is hidden).
  const fetchReportRef = useRef(fetchReport);
  fetchReportRef.current = fetchReport;
  const isLive = LIVE_CAMPAIGN_STATUSES.has(Number(campaign?.campaign_status));
  useEffect(() => {
    if (!isLive) return undefined;
    const timer = setInterval(() => {
      if (document.hidden) return;
      fetchReportRef.current({ includeSummary: true, silent: true });
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [isLive]);

  const showPulses = rows.some((r) => Array.isArray(r.pulses_breakdown));
  const pulsesText = (r) => {
    const list = Array.isArray(r?.pulses_breakdown) ? r.pulses_breakdown : [];
    if (!list.length) return '-';
    if (showCompanyNames) return list.map((e) => `${e.company_name || e.company_id}: ${e.value}`).join('; ');
    return String(Number(list.reduce((sum, e) => sum + (Number(e.value) || 0), 0).toFixed(4)));
  };
  // Same shape per leg; absent pulses_breakdown (no billing access) -> '-'.
  const legPulsesText = (leg) => pulsesText(leg);

  const callTypeBody = (leg) => {
    const type = String(leg?.cdr_call_type || '').toUpperCase();
    if (!type) return '—';
    const label = type.replace(/_/g, ' ');
    if (type.startsWith('TRANSFER'))
      return <Badge className="bg-violet-600 px-1.5 py-0 text-[10px] text-white hover:bg-violet-600">{label}</Badge>;
    if (type === 'CONFERENCE')
      return <Badge className="bg-sky-600 px-1.5 py-0 text-[10px] text-white hover:bg-sky-600">{label}</Badge>;
    return label;
  };

  const LEG_COLS = [
    { label: 'Call ID', min: 150, render: (l) => l.call_sid || '—' },
    { label: 'Call Type', min: 110, render: callTypeBody },
    { label: 'Leg Type', min: 90, agentOnly: true, render: (l) => l.leg_type || '—' },
    {
      label: 'Start / End',
      min: 170,
      sort: 'customer_start_time',
      render: (l) => (
        <>
          {formatDateTime(l.start_time)}
          <div>{formatDateTime(l.end_time)}</div>
        </>
      ),
    },
    { label: 'Total Duration', min: 100, render: (l) => formatDuration(legTotalOf(l)) },
    { label: 'Talk Time', min: 90, sort: 'customer_patch_duration', render: (l) => formatDuration(l.patch_duration) },
    { label: 'Ring', min: 70, sort: 'customer_ring_duration', render: (l) => formatDuration(l.ring_duration) },
    { label: 'Hold', min: 70, render: (l) => formatDuration(l.hold_duration) },
    { label: 'Mute', min: 70, render: (l) => formatDuration(l.mute_duration) },
    { label: 'Call Status', min: 100, render: legStatusOf },
    { label: 'Failure Reason', min: 130, render: (l) => l.call_failure_reason || '—' },
    { label: 'Hangup Reason', min: 150, sort: 'hangup_cause', render: (l) => hangupCauseLabel(l.hangup_cause) },
    ...(showPulses ? [{ label: 'Pulses', min: 80, render: legPulsesText }] : []),
  ];
  const customerCols = LEG_COLS.filter((c) => !c.agentOnly);
  const agentCols = [
    {
      label: 'Agent',
      min: 170,
      sort: 'agent_name',
      render: (l) => (
        <>
          <div>
            {l.transfer_to_name || l.agent_name || '—'}
            {l.agent_number ? <span className="ml-1 font-normal text-muted-foreground">{l.agent_number}</span> : null}
          </div>
          {l.transferred_by ? (
            <div className="text-[10px] font-normal text-muted-foreground">Transferred by {l.transferred_by}</div>
          ) : null}
        </>
      ),
    },
    ...LEG_COLS.map(({ sort: _sort, ...c }) => c),
  ];
  const detailColCount = showPulses ? 6 : 5;
  const colCount = 3 + customerCols.length + agentCols.length + detailColCount;

  // One fixed-height line per agent leg so every agent column lines up.
  const agentLegLines = (row, render) => {
    const legs = agentLegsOf(row);
    if (!legs.length) return '—';
    return legs.map((leg, i) => (
      <div
        key={leg.call_sid || i}
        className={
          'flex h-9 flex-col justify-center overflow-hidden ' +
          (i ? 'border-t border-border/40 ' : '') +
          (leg.call_sid && leg.call_sid === row.primary_agent_call_sid ? 'font-semibold' : '')
        }
      >
        {render(leg)}
      </div>
    ));
  };

  const ivrBody = (row) => {
    const steps = ivrStepsOf(row);
    if (!row.ivr_name && !steps.length) return '—';
    return (
      <div title={steps.join(' → ')}>
        {row.ivr_name ? <div className="font-medium">{row.ivr_name}</div> : null}
        {steps.length ? (
          <div className="text-[11px] text-muted-foreground">
            {steps.slice(0, 3).join(' → ')}
            {steps.length > 3 ? ` +${steps.length - 3} more` : ''}
          </div>
        ) : null}
      </div>
    );
  };

  const agentsDialedBody = (row) => {
    const text = agentsDialedText(row);
    const count = Number(row.agents_dialed_count ?? row.agents_dialed?.length ?? 0);
    if (!count && !text) return '—';
    return (
      <div className="max-w-[240px] truncate" title={text}>
        <span className="font-semibold">{count}</span>
        {text ? ` · ${text}` : ''}
      </div>
    );
  };

  const customerNumberOf = (row) => (row.direction === 'incoming' ? row.caller_number : row.callee_number);
  const didNumberOf = (row) => (row.direction === 'incoming' ? row.callee_number : row.caller_number);

  const openDetail = (row) =>
    setDetailRow({
      ...row,
      start_time: row.customer_leg?.start_time || row.start_time,
      end_time: row.customer_leg?.end_time || row.end_time,
      call_type: row.direction === 'incoming' ? 'incoming' : 'outgoing',
      did: didNumberOf(row),
    });

  const updateFilters = (patch) => {
    summaryLoadedRef.current = false;
    setPage(1);
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const applySearch = () => updateFilters({ search: draftSearch.trim() });

  const resetFilters = () => {
    setDraftSearch('');
    updateFilters({
      search: '',
      direction: 'all',
      call_status: 'all',
      hangup_cause: '',
      start_date: '',
      end_date: '',
    });
  };

  const handleSortClick = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const sortIndicator = (field) => {
    if (sortBy !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const exportCurrentPage = () => {
    if (!rows.length) {
      toast.info('Nothing to export on this page');
      return;
    }
    const legFields = [
      ['Call ID', (l) => l.call_sid || '-'],
      ['SIP Call ID', (l) => l.sip_call_id || '-'],
      ['Call Type', (l) => l.cdr_call_type || '-'],
      ['Leg Type', (l) => l.leg_type || '-'],
      ['Start', (l) => formatDateTime(l.start_time)],
      ['End', (l) => formatDateTime(l.end_time)],
      ['Total (s)', legTotalOf],
      ['Talk (s)', (l) => l.patch_duration ?? 0],
      ['Ring (s)', (l) => l.ring_duration ?? 0],
      ['Hold (s)', (l) => l.hold_duration ?? 0],
      ['Mute (s)', (l) => l.mute_duration ?? 0],
      ['Status', legStatusOf],
      ['Failure Reason', (l) => l.call_failure_reason || '-'],
      ['Hangup Reason', (l) => hangupCauseLabel(l.hangup_cause)],
      ...(showPulses ? [['Pulses', legPulsesText]] : []),
    ];
    const agentFields = [
      ['Name', (l) => l.transfer_to_name || l.agent_name || '-'],
      ['Number', (l) => l.agent_number || '-'],
      ['Transferred By', (l) => l.transferred_by || '-'],
      ...legFields.filter(([label]) => label !== 'SIP Call ID'),
    ];
    exportToCSV({
      fileName: `campaign-${campaignId}-report-page-${page}`,
      data: rows,
      columns: [
        { header: 'Trace ID', accessor: (r) => r.trace_id || '-' },
        { header: 'Customer Number', accessor: (r) => customerNumberOf(r) || '-' },
        { header: 'DID / CLI', accessor: (r) => didNumberOf(r) || '-' },
        { header: 'Call Status', accessor: (r) => callStatusOf(r).toUpperCase() },
        ...legFields.map(([label, get]) => ({
          header: `Customer ${label}`,
          accessor: (r) => (r.customer_leg ? get(r.customer_leg) : '-'),
        })),
        ...agentFields.map(([label, get]) => ({
          header: `Agent ${label}`,
          accessor: (r) => agentLegsOf(r).map(get).join(' | ') || '-',
        })),
        { header: 'Agent Legs Count', accessor: (r) => agentLegsOf(r).length },
        { header: 'Agents Dialed Count', accessor: (r) => Number(r.agents_dialed_count ?? r.agents_dialed?.length ?? 0) },
        { header: 'Agents Dialed', accessor: (r) => agentsDialedText(r) || '-' },
        { header: 'Call Group', accessor: (r) => r.call_group_name || r.queue_name || '-' },
        { header: 'IVR Name', accessor: (r) => r.ivr_name || '-' },
        { header: 'IVR Journey', accessor: (r) => ivrStepsOf(r).join('; ') || '-' },
        { header: 'Keys Pressed', accessor: (r) => r.dtmf || '-' },
        { header: 'Trunk', accessor: (r) => r.trunk || '-' },
        ...(showPulses ? [{ header: 'Total Pulses', accessor: (r) => pulsesText(r) }] : []),
      ],
    });
  };

  const statusLabel = useMemo(() => {
    const status = Number(campaign?.campaign_status);
    return CAMPAIGN_STATUS_LABEL[status] ?? '—';
  }, [campaign]);

  const connectRate = useMemo(() => {
    if (!summary?.total_calls) return '0%';
    return `${((summary.connected_calls / summary.total_calls) * 100).toFixed(1)}%`;
  }, [summary]);

  const numberBody = (row) => (
    <div>
      <div className="fw-semibold">{customerNumberOf(row) || '—'}</div>
      <small className="text-muted">{didNumberOf(row) || '—'}</small>
    </div>
  );

  const legBody = (row) => (
    <Badge
      className={
        row.direction === 'incoming'
          ? 'bg-sky-500 text-white hover:bg-sky-500'
          : 'bg-primary text-primary-foreground'
      }
    >
      {row.direction === 'incoming' ? 'Incoming' : 'Outbound'}
    </Badge>
  );

  const statusBody = (row) => {
    const status = callStatusOf(row);
    if (status === 'Connected') return <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">Connected</Badge>;
    if (status === 'Abandoned') return <Badge className="bg-amber-500 text-white hover:bg-amber-500">Abandoned</Badge>;
    return <Badge variant="secondary">{status}</Badge>;
  };

  const recordingBody = (row) => {
    const recordings = parseRecordings(row.recording_file);
    if (!recordings.length) return <span className="text-muted">—</span>;
    return (
      <div className="d-flex flex-column gap-1">
        {recordings.map((rec, idx) => (
          <a key={`${row.call_sid || row.id}-rec-${idx}`} href={rec.url} target="_blank" rel="noreferrer">
            <FiMic /> Play
          </a>
        ))}
      </div>
    );
  };

  if (loading && !campaign) return <Loader />;

  const GROUP_START = 'border-l border-border/60';
  const sortTh = (field, label, minWidth, extra = '') => (
    <th
      key={label}
      className={`whitespace-nowrap px-3 py-2 text-left cursor-pointer hover:text-foreground ${extra}`}
      style={{ minWidth }}
      onClick={() => handleSortClick(field)}
    >
      {label}
      {sortIndicator(field)}
    </th>
  );
  const plainTh = (label, minWidth, extra = '') => (
    <th key={label} className={`whitespace-nowrap px-3 py-2 text-left ${extra}`} style={{ minWidth }}>
      {label}
    </th>
  );
  const colTh = (c, i) => (c.sort ? sortTh(c.sort, c.label, c.min, i ? '' : GROUP_START) : plainTh(c.label, c.min, i ? '' : GROUP_START));
  const LEG_TD = 'whitespace-nowrap px-3 py-2 text-xs tabular-nums text-foreground';

  const totalPages = Math.max(1, Math.ceil(totalRecords / limit));

  return (
    <FillPage>
      <PageHeader
        title={campaign?.campaign_name || `Campaign #${campaignId}`}
        description={`ID: ${campaignId} · ${
          DIALER_TYPE_LABEL[Number(campaign?.campaign_type)] || '—'
        } · ${TEMPLATE_LABEL[Number(campaign?.template_id)] ?? '—'}${
          campaign?.first_call ? ` · First Call: ${campaign.first_call}` : ''
        } · Status: ${statusLabel}`}
        actions={
          <>
            <BsButton
              variant="outline"
              size="sm"
              onClick={() => navigate(`/${ROUTES.CAMPAIGNS.LIST}`)}
              className="gap-1.5"
            >
              <FiArrowLeft />
              Back
            </BsButton>
            <BsButton
              variant="outline"
              size="sm"
              onClick={exportCurrentPage}
            >
              Export
            </BsButton>
            <BsButton
              size="sm"
              disabled={rowsLoading}
              onClick={() => {
                summaryLoadedRef.current = false;
                fetchReport({ includeSummary: true });
              }}
              className="gap-1.5"
            >
              <FiRefreshCw className={rowsLoading ? 'animate-spin' : ''} />
              Refresh
            </BsButton>
          </>
        }
      />
      <div className="campaign-report-page mt-4">

      {/* ── Campaign totals (Postgres) + call aggregates (ClickHouse) ── */}
      <Card className="mb-3">
        <CardContent>
          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Campaign Details</div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            <StatTile icon={<FiHash />} label="Total Contacts" value={Number(campaign?.total_nos || 0)} tone="blue" />
            <StatTile icon={<FiPhone />} label="Processed" value={Number(campaign?.nos_processed || 0)} tone="teal" />
            <StatTile icon={<FiSlash />} label="DnD Skipped" value={Number(campaign?.dnd_count || 0)} tone="red" />
            <StatTile
              icon={<FiClock />}
              label="Auto Retries"
              value={Number(campaign?.auto_retry_count || 0)}
              hint={`Max ${Number(campaign?.max_retries || 0)}`}
              tone="violet"
            />
            <StatTile icon={<FiUsers />} label="Unique Numbers Dialed" value={Number(summary?.unique_numbers || 0)} tone="teal" />
          </div>

          <div className="mb-3 mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Schedule</div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            <StatTile icon={<FiClock />} label="Start Date" value={formatDateOnly(campaign?.start_date)} tone="blue" />
            <StatTile icon={<FiClock />} label="End Date" value={formatDateOnly(campaign?.end_date)} tone="blue" />
            <StatTile
              icon={<FiClock />}
              label="Calling Hours"
              value={`${formatTimeOnly(campaign?.start_hours)} – ${formatTimeOnly(campaign?.end_hours)}`}
              tone="violet"
            />
            {/* <StatTile icon={<FiPhone />} label="Concurrent Lines" value={Number(campaign?.channels || 0)} tone="teal" /> */}
            <StatTile
              icon={<FiRefreshCw />}
              label="Auto Retry"
              value={Number(campaign?.is_retry) ? 'On' : 'Off'}
              hint={Number(campaign?.is_retry) ? `Every ${Number(campaign?.retry_interval || 0)} min` : undefined}
              tone="violet"
            />
          </div>

          <div className="mb-3 mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Call Details</div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            <StatTile
              icon={<FiPhoneCall />}
              label="Total Calls"
              value={Number(summary?.total_calls || 0)}
              tone="blue"
            />
            <StatTile
              icon={<FiCheckCircle />}
              label="Connected"
              value={Number(summary?.connected_calls || 0)}
              hint={`${connectRate} of calls`}
              tone="green"
            />
            <StatTile icon={<FiXCircle />} label="Failed" value={Number(summary?.failed_calls || 0)} tone="red" />
            <StatTile
              icon={<FiPhoneOff />}
              label="Abandoned"
              value={Number(summary?.abandoned_calls || 0)}
              tone="orange"
            />
            <StatTile icon={<FiSlash />} label="DnD Calls" value={Number(summary?.dnd_calls || 0)} tone="red" />
            {/* Billing duration removed from this report.
            <StatTile
              icon={<FiClock />}
              label="Talk Time"
              value={formatDuration(summary?.total_billing_duration)}
              hint={`Avg ${formatDuration(summary?.avg_billing_duration)}`}
              tone="orange"
            /> */}
            {/* <StatTile
              icon={<FiMic />}
              label="Recorded Calls"
              value={Number(summary?.recorded_calls || 0)}
              tone="blue"
            /> */}
          </div>
        </CardContent>
      </Card>

      {/* ── Filters ── */}
      <Card className="mb-3">
        <CardContent>
          <div className="row g-3 align-items-end">
            <div className="col-md-3 col-sm-12">
              <label className="form-label small fw-semibold">Search</label>
              <div className="d-flex gap-2">
                <Input
                  placeholder="Number, agent, call SID"
                  value={draftSearch}
                  onChange={(e) => setDraftSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applySearch();
                  }}
                />
                <BsButton size="sm" className="text-nowrap" onClick={applySearch}>
                  Go
                </BsButton>
              </div>
            </div>
            {/* <div className="col-md-2 col-sm-6">
              <label className="form-label small fw-semibold">Leg</label>
              <select
                className="form-control form-select form-select-sm"
                value={filters.direction}
                onChange={(e) => updateFilters({ direction: e.target.value })}
              >
                {DIRECTION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div> */}
            <div className="col-md-2 col-sm-6">
              <label className="form-label small fw-semibold">Call Status</label>
              <select
                className="form-control form-select form-select-sm"
                value={filters.call_status}
                onChange={(e) => updateFilters({ call_status: e.target.value })}
              >
                {CALL_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2 col-sm-6">
              <label className="form-label small fw-semibold">From</label>
              <CalendarDateInput
                value={filters.start_date || campaign?.start_date || ''}
                onChange={(e) => updateFilters({ start_date: e })}
                className="form-control form-control-sm"
              />
            </div>
            <div className="col-md-2 col-sm-6">
              <label className="form-label small fw-semibold">To</label>
              <CalendarDateInput
                value={filters.end_date || campaign?.end_date || ''}
                onChange={(e) => updateFilters({ end_date: e })}
                className="form-control form-control-sm"
              />
            </div>
            <div className="col-md-2 col-sm-6 d-flex">
              <BsButton variant="outline" size="sm" className="w-100" onClick={resetFilters}>
                Reset Filters
              </BsButton>
            </div>
          </div>
          {filters.hangup_cause ? (
            <div className="mt-2">
              <Badge className="bg-foreground text-background">
                Call End Reason: {hangupCauseLabel(filters.hangup_cause)}{' '}
                <span
                  role="button"
                  className="ms-2"
                  onClick={() => updateFilters({ hangup_cause: '' })}
                >
                  ×
                </span>
              </Badge>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* ── Call end reason chips: click to filter the table ── */}
      {hangupBreakdown.length > 0 && (
        <Card className="mb-3">
          <CardContent>
            <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Call End Reasons</div>
            <div className="flex flex-wrap gap-2">
              {hangupBreakdown.map((item) => {
                const active = filters.hangup_cause === item.hangup_cause;
                return (
                  <button
                    key={item.hangup_cause}
                    type="button"
                    className={
                      'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-xs transition-colors ' +
                      (active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border/60 bg-card text-foreground hover:bg-primary/10 hover:text-primary')
                    }
                    onClick={() =>
                      updateFilters({
                        hangup_cause: active || item.hangup_cause === 'UNKNOWN' ? '' : item.hangup_cause,
                      })
                    }
                  >
                    <span>{hangupCauseLabel(item.hangup_cause)}</span>
                    <span
                      className={
                        'rounded-full px-1.5 text-[10px] font-semibold tabular-nums ' +
                        (active
                          ? 'bg-primary-foreground/20'
                          : 'bg-muted text-muted-foreground')
                      }
                    >
                      {item.calls}
                    </span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div>
          <Card className="mb-3">
            <CardContent className="report-table-card">
              <div className="overflow-x-auto rounded-lg border border-border/60">
                <table className="w-full border-collapse text-sm" style={{ minWidth: '100%' }}>
                  <thead>
                    <tr className="bg-muted/70 text-[11px] font-semibold uppercase tracking-wide text-foreground">
                      <th colSpan={3} className="px-3 py-1.5 text-left">Call</th>
                      <th colSpan={customerCols.length} className={`px-3 py-1.5 text-left ${GROUP_START}`}>Customer Leg</th>
                      <th colSpan={agentCols.length} className={`px-3 py-1.5 text-left ${GROUP_START}`}>Agent Leg(s)</th>
                      <th colSpan={detailColCount} className={`px-3 py-1.5 text-left ${GROUP_START}`}>Call Details</th>
                    </tr>
                    <tr className="bg-muted/50 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {plainTh('Number', 160)}
                      {plainTh('Call Status', 110)}
                      {plainTh('Trace ID', 150)}
                      {customerCols.map(colTh)}
                      {agentCols.map(colTh)}
                      {plainTh('IVR', 180, GROUP_START)}
                      {sortTh('dtmf', 'Keys Pressed', 110)}
                      {sortTh('queue_name', 'Call Group', 140)}
                      {plainTh('Agents Dialed', 200)}
                      {plainTh('Trunk', 110)}
                      {showPulses ? plainTh('Total Pulses', 100) : null}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {rowsLoading ? (
                      <tr>
                        <td colSpan={colCount} className="px-3 py-6 text-center text-sm text-muted-foreground">
                          Loading…
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={colCount} className="px-3 py-6 text-center text-sm text-muted-foreground">
                          No call records found for this campaign
                        </td>
                      </tr>
                    ) : (
                      rows.map((row, idx) => (
                        <tr
                          key={row.trace_id || row.call_sid || row.id || idx}
                          className={
                            'cursor-pointer align-top transition-colors hover:bg-primary/[0.06] ' +
                            (idx % 2 === 1 ? 'bg-muted/30' : 'bg-card')
                          }
                          onClick={() => openDetail(row)}
                        >
                          <td className="px-3 py-2 text-foreground">{numberBody(row)}</td>
                          <td className="px-3 py-2 text-foreground">{statusBody(row)}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{row.trace_id || '—'}</td>
                          {customerCols.map((c, i) => (
                            <td key={c.label} className={`${LEG_TD} ${i ? '' : GROUP_START}`}>
                              {row.customer_leg ? c.render(row.customer_leg) : '—'}
                            </td>
                          ))}
                          {agentCols.map((c, i) => (
                            <td key={c.label} className={`${LEG_TD} ${i ? '' : GROUP_START}`}>
                              {agentLegLines(row, c.render)}
                            </td>
                          ))}
                          <td className={`px-3 py-2 text-xs text-foreground ${GROUP_START}`}>{ivrBody(row)}</td>
                          <td className="px-3 py-2 text-muted-foreground">{row.dtmf || '—'}</td>
                          <td className="px-3 py-2 text-muted-foreground">{row.call_group_name || row.queue_name || '—'}</td>
                          <td className="px-3 py-2 text-xs text-foreground">{agentsDialedBody(row)}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{row.trunk || '—'}</td>
                          {showPulses ? <td className="px-3 py-2 tabular-nums text-foreground">{pulsesText(row)}</td> : null}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2">
                <div className="d-flex align-items-center gap-2">
                  <span className="small text-muted">Rows per page:</span>
                  <select
                    className="form-control form-select form-select-sm"
                    style={{ width: '90px' }}
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <span className="small text-muted">
                    Showing {rows.length === 0 ? 0 : (page - 1) * limit + 1}–
                    {(page - 1) * limit + rows.length} of {totalRecords}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-1">
                  <BsButton
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(1)}
                    title="First"
                  >
                    «
                  </BsButton>
                  <BsButton
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    title="Previous"
                  >
                    ‹
                  </BsButton>
                  <span className="px-2 text-sm font-semibold">{page}</span>
                  <BsButton
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    title="Next"
                  >
                    ›
                  </BsButton>
                  <BsButton
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage(totalPages)}
                    title="Last"
                  >
                    »
                  </BsButton>
                </div>
              </div>
            </CardContent>
          </Card>
      </div>

    </div>
      <CdrCallDetailDialog
        row={detailRow}
        did={detailRow?.did || '-'}
        pageRows={NO_PAGE_ROWS}
        userRole={userRole}
        hangupCauseMap={hangupCauseMap}
        transcriptLines={0}
        onClose={() => setDetailRow(null)}
      />
    </FillPage>
  );
};

export default CampaignReport;
